import { StylesConfigDto } from '../dto/styles';
import { CURRENT_STYLE_VERSION } from '../migrations/style/registry';

export const defaultStyleConfig: StylesConfigDto = {
  version: CURRENT_STYLE_VERSION,

  colors: {
    color1: {
      name: 'Primary',
      value: { r: 98, g: 69, b: 242, a: 1 },
    },
    color2: {
      name: 'Secondary',
      value: { r: 184, g: 255, b: 34, a: 1 },
    },
    color3: {
      name: 'Tertiary',
      value: { r: 153, g: 105, b: 215, a: 1 },
    },
    color4: {
      name: 'Text',
      value: { r: 0, g: 0, b: 0, a: 1 },
    },
    color5: {
      name: 'Accent',
      value: { r: 220, g: 220, b: 220, a: 1 },
    },
    color6: {
      name: 'Background',
      value: { r: 255, g: 255, b: 255, a: 1 },
    },
  },

  typography: {
    fontSizeUnit: 'px',

    heading: {
      fontFamily: 'Inter',
      h1: {
        letterSpacing: 0,
        fontWeight: '700',
        lineHeight: 1.2,
        fontColor: { r: 0, g: 0, b: 0, a: 1 },
        backgroundColor: null,
        desktop: {
          fontSize: 48,
        },
        tablet: {
          fontSize: 40,
        },
        mobile: {
          fontSize: 32,
        },
      },
      h2: {
        letterSpacing: 0,
        fontWeight: '600',
        lineHeight: 1.3,
        fontColor: { r: 0, g: 0, b: 0, a: 1 },
        backgroundColor: null,
        desktop: {
          fontSize: 40,
        },
        tablet: {
          fontSize: 32,
        },
        mobile: {
          fontSize: 28,
        },
      },
      h3: {
        letterSpacing: 0,
        fontWeight: '600',
        lineHeight: 1.3,
        fontColor: { r: 0, g: 0, b: 0, a: 1 },
        backgroundColor: null,
        desktop: {
          fontSize: 32,
        },
        tablet: {
          fontSize: 28,
        },
        mobile: {
          fontSize: 24,
        },
      },
      h4: {
        letterSpacing: 0,
        fontWeight: '500',
        lineHeight: 1.4,
        fontColor: { r: 0, g: 0, b: 0, a: 1 },
        backgroundColor: null,
        desktop: {
          fontSize: 24,
        },
        tablet: {
          fontSize: 22,
        },
        mobile: {
          fontSize: 20,
        },
      },
      h5: {
        letterSpacing: 0,
        fontWeight: '500',
        lineHeight: 1.4,
        fontColor: { r: 0, g: 0, b: 0, a: 1 },
        backgroundColor: null,
        desktop: {
          fontSize: 20,
        },
        tablet: {
          fontSize: 18,
        },
        mobile: {
          fontSize: 16,
        },
      },
      h6: {
        letterSpacing: 0,
        fontWeight: '500',
        lineHeight: 1.4,
        fontColor: { r: 0, g: 0, b: 0, a: 1 },
        backgroundColor: null,
        desktop: {
          fontSize: 16,
        },
        tablet: {
          fontSize: 16,
        },
        mobile: {
          fontSize: 14,
        },
      },
    },

    body: {
      fontFamily: 'Roboto',
      small: {
        fontSize: 12,
        fontColor: { r: 0, g: 0, b: 0, a: 1 },
        letterSpacing: 0,
        fontWeight: '400',
        lineHeight: 1.5,
        backgroundColor: null,
      },
      medium: {
        fontSize: 14,
        fontColor: { r: 0, g: 0, b: 0, a: 1 },
        letterSpacing: 0,
        fontWeight: '400',
        lineHeight: 1.5,
        backgroundColor: null,
      },
      large: {
        fontSize: 18,
        fontColor: { r: 0, g: 0, b: 0, a: 1 },
        letterSpacing: 0,
        fontWeight: '400',
        lineHeight: 1.5,
        backgroundColor: null,
      },
    },

    link: {
      link1: {
        fontColor: { r: 98, g: 69, b: 242, a: 1 },
        backgroundColor: null,
        fontWeight: '400',
        decoration: 'underline',
      },
      link2: {
        fontColor: { r: 153, g: 105, b: 215, a: 1 },
        backgroundColor: null,
        fontWeight: '400',
        decoration: 'underline',
      },
    },
  },

  buttons: {
    fontSizeUnit: 'px',

    button1: {
      fontFamily: 'Inter',
      fontColor: { r: 247, g: 245, b: 246, a: 1 },
      backgroundColor: '$ref:#/colors/color1',
      borderColor: null,
      backgroundHoverColor: null,
      borderHoverColor: null,
      fontHoverColor: null,
      fontWeight: '500',
      letterSpacing: 0,
      borderWidth: 0,
      small: {
        borderRadius: [6, 6, 6, 6],
        fontSize: 12,
        padding: [4, 10, 4, 10],
        margin: [0, 0, 0, 0],
      },
      medium: {
        borderRadius: [6, 6, 6, 6],
        fontSize: 16,
        padding: [6, 16, 6, 16],
        margin: [0, 0, 0, 0],
      },
      large: {
        borderRadius: [6, 6, 6, 6],
        fontSize: 20,
        padding: [8, 22, 8, 22],
        margin: [0, 0, 0, 0],
      },
    },

    button2: {
      fontFamily: 'Inter',
      fontColor: { r: 17, g: 17, b: 18, a: 1 },
      backgroundColor: '$ref:#/colors/color2',
      borderColor: null,
      backgroundHoverColor: null,
      borderHoverColor: null,
      fontHoverColor: null,
      fontWeight: '500',
      letterSpacing: 0,
      borderWidth: 0,
      small: {
        borderRadius: [6, 6, 6, 6],
        fontSize: 12,
        padding: [4, 10, 4, 10],
        margin: [0, 0, 0, 0],
      },
      medium: {
        borderRadius: [6, 6, 6, 6],
        fontSize: 16,
        padding: [6, 16, 6, 16],
        margin: [0, 0, 0, 0],
      },
      large: {
        borderRadius: [6, 6, 6, 6],
        fontSize: 20,
        padding: [8, 22, 8, 22],
        margin: [0, 0, 0, 0],
      },
    },

    button3: {
      fontFamily: 'Inter',
      fontColor: { r: 17, g: 17, b: 18, a: 1 },
      backgroundColor: '$ref:#/colors/color3',
      borderColor: null,
      backgroundHoverColor: null,
      borderHoverColor: null,
      fontHoverColor: null,
      fontWeight: '500',
      letterSpacing: 0,
      borderWidth: 0,
      small: {
        borderRadius: [6, 6, 6, 6],
        fontSize: 12,
        padding: [4, 10, 4, 10],
        margin: [0, 0, 0, 0],
      },
      medium: {
        borderRadius: [6, 6, 6, 6],
        fontSize: 16,
        padding: [6, 16, 6, 16],
        margin: [0, 0, 0, 0],
      },
      large: {
        borderRadius: [6, 6, 6, 6],
        fontSize: 20,
        padding: [8, 22, 8, 22],
        margin: [0, 0, 0, 0],
      },
    },
  },
};
